/**
 * Runs async tasks one at a time per key. A burst of clicks on the same
 * button (three quick "Attempt" taps before a top) lands as three sequential
 * requests instead of racing each other — each one sees the result the last
 * one left behind, rather than three copies all reasoning from "no result
 * yet". A different key runs independently, with no such wait.
 *
 * The previous task's own success or failure never stops the next one from
 * running; only the caller of *that* task sees its outcome.
 */
export function createSerialQueue() {
  const tails = new Map<string, Promise<unknown>>();
  // Tasks queued or running, and tasks ever queued, across every key: what
  // `mark` and `quietSince` answer from.
  let pending = 0;
  let queued = 0;
  const settle = () => {
    pending -= 1;
  };

  function runSerially<T>(key: string, task: () => Promise<T>): Promise<T> {
    pending += 1;
    queued += 1;
    const previous = tails.get(key) ?? Promise.resolve();
    const settledPrevious = previous.then(
      () => undefined,
      () => undefined
    );
    const run = settledPrevious.then(task);
    tails.set(
      key,
      run.then(settle, settle)
    );
    return run;
  }

  /**
   * For a page's periodic refresh, which must not put on screen an answer read
   * while a tap was on its way — it would undo the tap, and the cards the next
   * one reasons from with it. `mark()` before asking the server (null while
   * anything is queued: there is no quiet moment to start from), then
   * `quietSince(mark)` once the answer is in: true only if nothing ran in
   * between and nothing is running now.
   */
  runSerially.mark = (): number | null => (pending === 0 ? queued : null);
  runSerially.quietSince = (mark: number | null) => mark !== null && pending === 0 && queued === mark;

  return runSerially;
}

export type SerialQueue = ReturnType<typeof createSerialQueue>;
